import PropTypes from 'prop-types';
import { alpha, Box, Button, FormHelperText, Grid, IconButton, Typography } from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import { Field, FieldArray } from 'formik';
import AppTextField from '@projectK/components/AppTextField';
import { getOptionsErrorMessage } from './profileFieldFormUtils';

/** Answer choices block for global Manage Profile Fields add/edit modals */
const GlobalProfileFieldOptionsSection = ({ values, touched, errors }) => {
  const optionsTouched = touched?.options;
  const optionsErrorMessage = getOptionsErrorMessage(errors?.options);
  const showOptionsError = Boolean(optionsTouched && optionsErrorMessage);

  return (
    <FieldArray name='options'>
      {({ push: pushOption, remove: removeOption }) => (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 1.5,
            bgcolor: showOptionsError ? alpha('#d32f2f', 0.06) : 'grey.50',
            border: '1px dashed',
            borderColor: showOptionsError ? 'error.main' : 'grey.300',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}
          >
            <Box>
              <Typography variant='subtitle2' fontWeight={600}>
                Options
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Required for single-select and multi-select field types
              </Typography>
            </Box>
            <Button
              type='button'
              size='small'
              startIcon={<AddCircleOutline />}
              onClick={() =>
                pushOption(
                  values?.options?.[0]?.option_id !== undefined
                    ? { name: '', option_id: null }
                    : { name: '' },
                )
              }
            >
              Add Option
            </Button>
          </Box>

          {showOptionsError && !values?.options?.length && (
            <FormHelperText error sx={{ mb: 1.5 }}>
              {optionsErrorMessage}
            </FormHelperText>
          )}

          <Grid container spacing={1.5}>
            {(values?.options || []).map((option, optionIndex) => {
              const choiceTouched = touched?.options?.[optionIndex]?.name;
              const choiceError = errors?.options?.[optionIndex]?.name;
              return (
                <Grid size={{ xs: 12 }} key={`global-opt-${optionIndex + 1}`}>
                  <Box display='flex' alignItems='flex-start' gap={1}>
                    <Field
                      name={`options[${optionIndex}].name`}
                      as={AppTextField}
                      label={`Option ${optionIndex + 1}`}
                      fullWidth
                      error={choiceTouched && Boolean(choiceError)}
                      helperText={choiceTouched && choiceError ? choiceError : ''}
                      inputProps={{ maxLength: 101 }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton
                      type='button'
                      size='small'
                      color='error'
                      aria-label={`Remove choice ${optionIndex + 1}`}
                      onClick={() => removeOption(optionIndex)}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </FieldArray>
  );
};

GlobalProfileFieldOptionsSection.propTypes = {
  values: PropTypes.object.isRequired,
  touched: PropTypes.object,
  errors: PropTypes.object,
};

export default GlobalProfileFieldOptionsSection;
